import { BlurFilter, Container, Graphics, Sprite, Text } from 'pixi.js';
import type { CurrencyFormat, ResolvedTheme, Strings } from '../types';
import { alphaOf, toPixiColor } from '../util/color';
import { formatMoney, t } from '../util/format';
import { Easings, type Timeline } from './tween';
import type { TextureStore } from './textures';

/**
 * Win plaque shown over the reel console 1.5 s after the last reel stops (ClefDev win-plaque pack):
 * themed PNG backplate (3:2) + DOM-free text layers (label · amount · detail) positioned per the pack's
 * manifest, bounce-in around the plaque's (50%, 60%) point, static accent glow afterwards.
 * Themes without `assets.outcomeCard.win` fall back to a drawn card. Losses show nothing.
 */
export class OutcomeCard extends Container {
  private theme: ResolvedTheme;
  private strings: Strings;
  private readonly currency: CurrencyFormat;
  private readonly textures: TextureStore | null;
  /** Scaled/rotated during the bounce; its origin sits at the plaque's (50%, 60%) point. */
  private readonly pivot_ = new Container();
  private readonly bg = new Graphics();
  private readonly glowGfx = new Graphics();
  private art: Sprite | null = null;
  private artGlow: Sprite | null = null;
  private readonly labelText: Text;
  private readonly amountText: Text;
  private readonly subText: Text;
  private width_ = 300;
  private reduced = false;
  /** Incremented by hide(); show() bails out after its awaits if it is no longer the current presentation. */
  private showId = 0;

  constructor(o: { theme: ResolvedTheme; strings: Strings; currency: CurrencyFormat; reducedMotion?: boolean; textures?: TextureStore }) {
    super();
    this.theme = o.theme;
    this.strings = o.strings;
    this.currency = o.currency;
    this.textures = o.textures ?? null;
    this.reduced = !!o.reducedMotion;
    const display = o.theme.fonts.display.family;
    this.labelText = new Text({ text: '', style: { fontFamily: display, fontSize: 12, fontWeight: '800', fill: 0xffffff, letterSpacing: 1.2, align: 'center' } });
    this.amountText = new Text({ text: '', style: { fontFamily: display, fontSize: 40, fontWeight: '800', fill: 0xfff8dc, letterSpacing: -1, align: 'center' } });
    this.subText = new Text({ text: '', style: { fontFamily: o.theme.fonts.ui.family, fontSize: 12, fontWeight: '600', fill: 0xd0e4e6, align: 'center' } });
    for (const tx of [this.labelText, this.amountText, this.subText]) {
      tx.anchor.set(0.5);
      tx.resolution = 2;
    }
    this.addChild(this.pivot_);
    this.buildArt();
    this.pivot_.addChild(this.labelText, this.amountText, this.subText);
    this.visible = false;
    this.eventMode = 'none';
  }

  private get hasArt(): boolean {
    return !!this.art;
  }

  private buildArt(): void {
    this.art?.destroy();
    this.artGlow?.destroy();
    this.art = null;
    this.artGlow = null;
    for (const c of [this.glowGfx, this.bg]) if (c.parent) this.pivot_.removeChild(c);
    const url = this.theme.assets.outcomeCard?.win;
    const tex = url && this.textures ? this.textures.image(url) : null;
    if (tex) {
      this.artGlow = new Sprite(tex);
      this.artGlow.anchor.set(0.5);
      this.artGlow.filters = [new BlurFilter({ strength: 6, quality: 3 })];
      this.artGlow.alpha = 0.85;
      this.art = new Sprite(tex);
      this.art.anchor.set(0.5);
      this.pivot_.addChildAt(this.artGlow, 0);
      this.pivot_.addChildAt(this.art, 1);
    } else {
      this.pivot_.addChildAt(this.glowGfx, 0);
      this.pivot_.addChildAt(this.bg, 1);
    }
  }

  setTheme(theme: ResolvedTheme): void {
    this.theme = theme;
    this.labelText.style.fontFamily = theme.fonts.display.family;
    this.amountText.style.fontFamily = theme.fonts.display.family;
    this.subText.style.fontFamily = theme.fonts.ui.family;
    this.buildArt();
    this.layout(this.width_);
  }

  setStrings(strings: Strings): void {
    this.strings = strings;
  }

  private get accent(): number {
    return toPixiColor(this.theme.palette.plaqueAccent ?? this.theme.palette.winNormal);
  }

  /** Plaque width in design units (3:2); position is set by the presenter (centre of the reel console). */
  layout(width: number): void {
    this.width_ = width;
    const w = width;
    const h = this.hasArt ? (w * 2) / 3 : w * 0.62;
    // Bounce pivot at (50%, 60%) of the plaque: the artwork centre sits 10% of h above the pivot, and the
    // pivot itself is pushed down by the same amount so the plaque stays centred on the reel console.
    const oy = this.hasArt ? h * 0.1 : 0;
    this.pivot_.position.set(0, oy);
    if (this.art && this.artGlow) {
      this.art.width = w;
      this.art.height = h;
      this.art.position.set(0, -oy);
      this.artGlow.width = w * 1.03;
      this.artGlow.height = h * 1.03;
      this.artGlow.position.set(0, -oy);
      this.artGlow.tint = this.accent;
      const at = (ny: number) => -oy + (ny - 0.5) * h;
      this.labelText.style.fontSize = Math.max(9, w * 0.035);
      this.labelText.style.letterSpacing = w * 0.035 * 0.1;
      this.labelText.position.set(0, at(0.5));
      this.amountText.style.fontSize = w * 0.116;
      this.amountText.style.letterSpacing = -w * 0.116 * 0.035;
      this.amountText.position.set(0, at(0.605));
      this.subText.style.fontSize = Math.max(9, w * 0.031);
      this.subText.position.set(0, at(0.7));
      this.subText.visible = w >= 260 && this.subText.text !== '';
      this.fitAmount();
      return;
    }
    this.labelText.style.fontSize = Math.max(10, w * 0.045);
    this.labelText.style.letterSpacing = w * 0.009;
    this.amountText.style.fontSize = Math.max(22, w * 0.15);
    this.amountText.style.letterSpacing = -w * 0.006;
    this.subText.style.fontSize = Math.max(10, w * 0.046);
    this.subText.visible = this.subText.text !== '';
    this.redrawFallback(this.accent);
  }

  /** Never wrap, abbreviate or clip the amount: shrink the font until it fits the pack's safe zone (56% of width). */
  private fitAmount(): void {
    if (!this.hasArt) return;
    const max = this.width_ * 0.56;
    const base = this.width_ * 0.116;
    this.amountText.style.fontSize = base;
    const wText = this.amountText.width;
    if (wText > max && wText > 0) this.amountText.style.fontSize = Math.max(9, (base * max) / wText) * 0.98;
  }

  private redrawFallback(color: number): void {
    const w = this.width_;
    const h = w * 0.62;
    const r = Math.min(18, w * 0.07);
    this.bg
      .clear()
      .roundRect(-w / 2, -h / 2, w, h, r)
      .fill({ color: 0x060c12, alpha: 0.92 })
      .roundRect(-w / 2, -h / 2, w, h, r)
      .stroke({ color, alpha: 0.8, width: 1.5 });
    this.glowGfx.clear();
    for (let i = 1; i <= 4; i++) {
      this.glowGfx.roundRect(-w / 2 - i * 3, -h / 2 - i * 3, w + i * 6, h + i * 6, r + i * 3).stroke({ color, alpha: 0.12 - i * 0.02, width: 4 });
    }
    this.labelText.position.set(0, -h * 0.3);
    this.amountText.position.set(0, -h * 0.02);
    this.subText.position.set(0, h * 0.3);
  }

  /** Show the win plaque with the pack's bounce; resolves after the bounce (the plaque stays until hide()). */
  async show(amount: number, lines: number, timeline: Timeline): Promise<void> {
    this.hide();
    const id = ++this.showId;
    const p = this.theme.palette;
    const accent = this.accent;
    this.labelText.text = t(this.strings, 'outcomeWin').toUpperCase();
    this.labelText.style.fill = accent;
    this.amountText.text = '+' + formatMoney(amount, this.currency);
    this.amountText.style.fill = this.hasArt ? toPixiColor(p.plaqueAmountInk ?? '#fff8dc') : 0xffffff;
    this.subText.text = lines <= 0 ? '' : lines === 1 && this.strings.outcomeLine ? t(this.strings, 'outcomeLine') : t(this.strings, 'outcomeLines', { n: lines });
    if (this.hasArt) {
      this.subText.style.fill = toPixiColor(p.plaqueDetailInk ?? '#d0e4e6');
      this.subText.alpha = 1;
    } else {
      this.subText.style.fill = toPixiColor(p.textMuted);
      this.subText.alpha = alphaOf(p.textMuted);
      this.redrawFallback(accent);
    }
    this.layout(this.width_);
    this.visible = true;
    if (this.reduced) {
      this.pivot_.alpha = 1;
      this.pivot_.scale.set(1);
      this.pivot_.rotation = 0;
      return;
    }
    await timeline.tween({
      duration: 720,
      easing: Easings.linear,
      update: (raw) => {
        // keyframes from the pack: 0% .45/-5°  54% 1.12/1.5°  72% .94/-.7°  88% 1.035  100% 1
        const k = [
          [0, 0.45, -5, 0],
          [0.54, 1.12, 1.5, 1],
          [0.72, 0.94, -0.7, 1],
          [0.88, 1.035, 0, 1],
          [1, 1, 0, 1],
        ];
        let i = 0;
        while (i < k.length - 2 && raw > k[i + 1][0]) i++;
        const [t0, s0, r0, a0] = k[i];
        const [t1, s1, r1, a1] = k[i + 1];
        const u = t1 === t0 ? 1 : Math.min(1, Math.max(0, (raw - t0) / (t1 - t0)));
        const e = i === 0 ? Easings.cubicOut(u) : Easings.quadInOut(u);
        this.pivot_.scale.set(s0 + (s1 - s0) * e);
        this.pivot_.rotation = ((r0 + (r1 - r0) * e) * Math.PI) / 180;
        this.pivot_.alpha = a0 + (a1 - a0) * e;
      },
    });
    // The bounce may have been cancelled by clearWin()/setTheme(); never resurrect a hidden or destroyed card.
    if (id !== this.showId || !this.visible || this.destroyed) return;
    this.pivot_.scale.set(1);
    this.pivot_.rotation = 0;
    this.pivot_.alpha = 1;
  }

  hide(): void {
    this.showId++;
    if (this.destroyed) return;
    this.visible = false;
    this.pivot_.scale.set(1);
    this.pivot_.rotation = 0;
  }

  override destroy(): void {
    super.destroy({ children: true });
  }
}
